import React, { useEffect, useState } from 'react'
import {
    Box,
    Card,
    CardHeader,
    Container,
    Divider,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Button,
    MenuItem,
} from '@mui/material'
import { Formik } from 'formik'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import EmployeeDialog from '../Components/EmployeeDialog';
import { BRAND } from '../constants/brand';

const ROLE_OPTIONS = [
    { value: 0, label: 'All Roles' },
    { value: 1, label: 'Administartion' },
    { value: 2, label: 'Technician' },
    { value: 3, label: 'Accountant' },
    { value: 4, label: 'Store Keeper' },
];

function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}

TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
};

const getRoleLabel = (role) => {
    const found = ROLE_OPTIONS.find(option => option.value === role);
    return found ? found.label : 'Staff';
};

function EmployeeManagement() {
    const [pageTitle] = useState(`${BRAND.name} Employee Management`);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [employeeList, setEmployeeList] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState(0);
    const [updateID, setUpdateID] = useState(0);

    useEffect(() => {
        getEmployees();
    }, []);

    useEffect(() => {
        getEmployees();
    }, [dialogOpen]);

    useEffect(() => {
        filterEmployees(employeeSearch, roleFilter);
    }, [employeeSearch, roleFilter, employeeList]);

    function handleClick() {
        setDialogOpen(true);
    }

    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredEmployees.length) : 0;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function getEmployees() {
        axios.get('https://serverukautotech.ccsoftwares.com/Employees/getEmployees').then((res) => {
            if (res.data.length > 0) {
                setEmployeeList(res.data);
                filterEmployees(employeeSearch, roleFilter, res.data);
            } else {
                setEmployeeList([]);
                setFilteredEmployees([]);
                toast.error("No records to display!");
            }
        }).catch(() => {
            toast.error("Unable to fetch employees");
        });
    }

    function filterEmployees(searchText = "", role = roleFilter, data = employeeList) {
        const filtered = data.filter((employee) => {
            const matchSearch = searchText === "" || employee.EmpName.toLowerCase().includes(searchText.toLowerCase()) || employee.ContactNo.toString().includes(searchText);
            const matchRole = role === 0 || employee.role === role;
            return matchSearch && matchRole;
        });
        setFilteredEmployees(filtered);
    }

    function openForUpdate(id) {
        setUpdateID(id);
        setDialogOpen(true);
    }

    function deleteEmployee(id) {
        const confirmDelete = window.confirm("Do you want to delete this employee?");
        if (!confirmDelete) {
            return;
        }

        axios.delete(`https://serverukautotech.ccsoftwares.com/Employees/deleteEmployee/${id}`).then((res) => {
            if (res.data.affectedRows > 0) {
                toast.success("Employee deleted");
                getEmployees();
            }
        }).catch(() => {
            toast.error("Unable to delete employee");
        });
    }

    function cardTitle(titleName) {
        return (
            <Grid container spacing={1}>
                <Grid item md={11} xs={11}>
                    {titleName}
                </Grid>
                <Grid item md={1} xs={1}>
                    <Box display='flex' justifyContent='flex-end' alignItems='center' >
                        <Button variant="contained" endIcon={<AddCircleOutlineIcon />} onClick={() => handleClick()} sx={{ width: '100%' }}>
                            Add
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        )
    }

    return (
        <>
            <Container maxWidth={false} sx={{ paddingTop: '15vh' }}>
                <Formik
                    enableReinitialize
                >
                    {({
                        errors,
                        handleBlur,
                        handleSubmit,
                        touched
                    }) => (
                        <form>
                            <Box mt={0}>
                                <Card sx={{ padding: '15px' }}>
                                    <CardHeader
                                        title={cardTitle(pageTitle)}
                                    />
                                    <Divider />
                                    <br />
                                    <TableContainer>
                                        <Grid container spacing={2}>
                                            <Grid item md={6} sx={6}>
                                                <Box sx={{ display: 'flex', justifyContent: 'start' }}>
                                                    <SearchIcon sx={{ color: 'action.active', mr: 1, my: 1 }} />
                                                    <TextField
                                                        sx={{ width: { md: '50%', xs: '60%' } }}
                                                        label="Search by name or contact"
                                                        name='employeeSearch'
                                                        value={employeeSearch}
                                                        onChange={(e) => { setEmployeeSearch(e.target.value) }}
                                                        size='small'
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item md={6} sx={6}>
                                                <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        onBlur={handleBlur}
                                                        name='roleFilter'
                                                        value={roleFilter}
                                                        id='roleFilter'
                                                        onChange={(e) => { setRoleFilter(parseInt(e.target.value)) }}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ width: { md: '30%', xs: '60%' } }}
                                                    >
                                                        {ROLE_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <br />
                                        <Table sx={{ minWidth: 500 }} size="small" aria-label="employee table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }}>#</TableCell>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }} >Employee Name</TableCell>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }} >Contact Number</TableCell>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }} >Role</TableCell>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(rowsPerPage > 0
                                                    ? filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    : filteredEmployees
                                                ).map((row, index) => (
                                                    <TableRow key={row.empid}>
                                                        <TableCell align='center'>{index + 1 + page * rowsPerPage}</TableCell>
                                                        <TableCell align='center'>{row.EmpName}</TableCell>
                                                        <TableCell align='center'>{row.ContactNo}</TableCell>
                                                        <TableCell align='center'>{getRoleLabel(row.role)}</TableCell>
                                                        <TableCell align='center'>
                                                            <IconButton>
                                                                <CreateIcon sx={{ color: 'black' }} onClick={() => openForUpdate(row.empid)} />
                                                            </IconButton>
                                                            <IconButton>
                                                                <DeleteIcon sx={{ color: 'black' }} onClick={() => deleteEmployee(row.empid)} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {emptyRows > 0 && (
                                                    <TableRow style={{ height: 53 * emptyRows }}>
                                                        <TableCell colSpan={7} />
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                            <TableFooter>
                                                <TableRow>
                                                    <TablePagination
                                                        rowsPerPageOptions={[10, 25, 35, { label: 'All', value: -1 }]}
                                                        colSpan={7}
                                                        count={filteredEmployees.length}
                                                        rowsPerPage={rowsPerPage}
                                                        page={page}
                                                        slotProps={{
                                                            select: {
                                                                inputProps: {
                                                                    'aria-label': 'rows per page',
                                                                },
                                                                native: true,
                                                            },
                                                        }}
                                                        onPageChange={handleChangePage}
                                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                                        ActionsComponent={TablePaginationActions}
                                                    />
                                                </TableRow>
                                            </TableFooter>
                                        </Table>
                                    </TableContainer>
                                </Card>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Container>

            <EmployeeDialog
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                updateID={updateID}
                setUpdateID={setUpdateID}
            />

        </>
    )
}

export default EmployeeManagement
